import React, { useState, useEffect } from 'react';
import { SEO } from '../components/SEO';
import { FaDownload, FaFileAlt, FaChartLine, FaArrowRight, FaChevronDown, FaChevronUp, FaInfoCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const Downloads = () => {
  const [isSticky, setIsSticky] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [selectedVersion, setSelectedVersion] = useState('opus25');
  
  // Reset scroll position when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Función para manejar el scroll y actualizar la sección activa
  useEffect(() => {
    const handleScroll = () => {
      // Detectar si el submenú debe ser sticky
      const heroSection = document.getElementById('overview');
      const headerHeight = 64; // Altura del header principal
      
      if (heroSection) {
        const heroBottom = heroSection.getBoundingClientRect().bottom;
        setIsSticky(heroBottom <= headerHeight);
      } else {
        // Si no se encuentra la sección, mantener el menú sticky para evitar que desaparezca
        setIsSticky(true);
      }

      // Detectar qué sección está visible
      const sections = ['overview'];
      let activeFound = false;
      
      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = 140; // Altura del header + submenu
          if (rect.top <= offset && rect.bottom >= offset) {
            setActiveSection(section);
            activeFound = true;
            break;
          }
        }
      }
      
      // Si no se encuentra ninguna sección activa, mantener la última activa
      if (!activeFound && window.scrollY > 0) {
        setIsSticky(true);
      }
    };

    // Ejecutar handleScroll inmediatamente para establecer el estado inicial
    handleScroll();
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Renderiza diferentes contenidos basados en la versión seleccionada
  const renderVersionContent = () => {
    switch(selectedVersion) {
      case 'opus25':
        return (
          <div className="py-12 px-6">
            <div className="container mx-auto max-w-6xl">
              <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
                <div className="flex items-center mb-6">
                  <div className="bg-[#0055B8]/10 p-3 rounded-full mr-4">
                    <FaInfoCircle className="text-[#0055B8] text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1A2B39]">OPUS 25 - La versión más reciente</h2>
                    <p className="text-gray-600">Acceda a la plataforma integrada de descargas para OPUS 25.</p>
                  </div>
                </div>
                <p className="mb-6 text-gray-700">
                  OPUS 25 es la versión más reciente de nuestro software, que incluye las últimas innovaciones y mejoras para optimizar sus proyectos de construcción. A través de nuestro portal de descargas integrado, podrá acceder a todos los módulos y actualizaciones disponibles.
                </p>
                <div className="mb-8">
                  <h3 className="font-bold text-lg mb-2 text-[#1A2B39]">Características destacadas:</h3>
                  <ul className="list-disc pl-6 space-y-1 text-gray-700">
                    <li>Interfaz mejorada y más intuitiva</li>
                    <li>Mayor velocidad en operaciones con grandes volúmenes de datos</li>
                    <li>Nuevas herramientas de análisis y reportes</li>
                    <li>Integración avanzada con otras plataformas</li>
                    <li>Actualizaciones automáticas de precios</li>
                  </ul>
                </div>

                <div className="mb-8">
                  <h3 className="font-bold text-lg mb-2 text-[#1A2B39]">Documentación:</h3>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <a 
                      href="/descargas/opus25/manual_instal_opus25.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Manual de Instalación</p>
                        <p className="text-sm text-gray-600">Guía paso a paso para la instalación</p>
                      </div>
                    </a>
                    
                    <a 
                      href="/descargas/opus25/OPUS25_M1.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Novedades OPUS 25</p>
                        <p className="text-sm text-gray-600">Descubre las nuevas características</p>
                      </div>
                    </a>
                  </div>
                </div>

                <div className="mb-8">
                  <h3 className="font-bold text-lg mb-2 text-[#1A2B39]">Cambios y mejoras:</h3>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <Link 
                      to="/soporte/descargas/opus25/m1/changelog" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Cambios Módulo 1</p>
                        <p className="text-sm text-gray-600">Ver los cambios del Módulo 1</p>
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
              
              <iframe 
                src="https://descargas.ecosoft.com.mx/" 
                title="Portal de Descargas Ecosoft" 
                className="w-full h-[800px] border rounded shadow-lg"
                allow="fullscreen"
              />
            </div>
          </div>
        );
      case 'opus24':
        return (
          <div className="py-12 px-6">
            <div className="container mx-auto max-w-6xl">
              <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
                <div className="flex items-center mb-6">
                  <div className="bg-[#0055B8]/10 p-3 rounded-full mr-4">
                    <FaInfoCircle className="text-[#0055B8] text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1A2B39]">OPUS 24 - Versión estable con soporte completo</h2>
                    <p className="text-gray-600">Acceda a la plataforma integrada de descargas para OPUS 24.</p>
                  </div>
                </div>
                <p className="mb-6 text-gray-700">
                  OPUS 24 es nuestra versión estable y completamente soportada. Ideal para usuarios que requieren una plataforma probada y confiable para sus proyectos. A través de nuestro portal de descargas, podrá acceder a todos los módulos y actualizaciones disponibles.
                </p>
                <div className="mb-8">
                  <h3 className="font-bold text-lg mb-2 text-[#1A2B39]">Enlaces a documentación:</h3>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <Link 
                      to="/soporte/descargas/opus24/m1/changelog" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Cambios Módulo 1</p>
                        <p className="text-sm text-gray-600">Ver los cambios del Módulo 1</p>
                      </div>
                    </Link>
                    
                    <Link 
                      to="/soporte/descargas/opus24/m2/changelog" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Cambios Módulo 2</p>
                        <p className="text-sm text-gray-600">Ver los cambios del Módulo 2</p>
                      </div>
                    </Link>
                  </div>
                </div>
                
                <div className="mb-8">
                  <h3 className="font-bold text-lg mb-2 text-[#1A2B39]">Manuales y guías:</h3>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    <a 
                      href="/documents/Opus24/M1.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Manual Módulo 1</p>
                        <p className="text-sm text-gray-600">Presupuesto Programable</p>
                      </div>
                    </a>
                    
                    <a 
                      href="/documents/Opus24/M2.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Manual Módulo 2</p>
                        <p className="text-sm text-gray-600">Planeación y Control</p>
                      </div>
                    </a>
                    
                    <a 
                      href="/documents/Opus24/M3.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Manual Módulo 3</p>
                        <p className="text-sm text-gray-600">Administración de Compras</p>
                      </div>
                    </a>
                    
                    <a 
                      href="/documents/Opus24/M4.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Manual Módulo 4</p>
                        <p className="text-sm text-gray-600">Control de Estimaciones</p>
                      </div>
                    </a>
                    
                    <a 
                      href="/documents/Opus24/guia_llave_acceso.pdf" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="flex items-center p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors"
                    >
                      <div className="w-10 h-10 bg-[#0055B8]/10 rounded-full flex items-center justify-center mr-4">
                        <FaFileAlt className="text-[#0055B8]" />
                      </div>
                      <div>
                        <p className="font-bold text-[#1A2B39]">Guía para Llave de Acceso</p>
                        <p className="text-sm text-gray-600">Instructivo de instalación</p>
                      </div>
                    </a>
                  </div>
                </div>
              </div>
              
              <iframe 
                src="https://descargas.ecosoft.com.mx/" 
                title="Portal de Descargas OPUS 24" 
                className="w-full h-[800px] border rounded shadow-lg"
                allow="fullscreen"
              />
              <div className="mt-3 text-sm text-gray-600 text-center">
                <p>Portal oficial de descargas OPUS 24. Para descargar el programa, utilice directamente este portal.</p>
              </div>
            </div>
          </div>
        );
      case 'opus22':
        return (
          <div className="py-12 px-6">
            <div className="container mx-auto max-w-6xl">
              <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
                <div className="flex items-center mb-6">
                  <div className="bg-[#0055B8]/10 p-3 rounded-full mr-4">
                    <FaInfoCircle className="text-[#0055B8] text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1A2B39]">OPUS 22</h2>
                    <p className="text-gray-600">Versión para usuarios con suscripciones específicas</p>
                  </div>
                </div>
                <p className="mb-6 text-gray-700">
                  OPUS 22 está disponible para usuarios con suscripciones específicas. Esta versión sigue siendo compatible con proyectos existentes y ofrece todas las funcionalidades esenciales para la gestión de proyectos de construcción.
                </p>
              </div>
              
              <h2 className="text-3xl font-bold mb-8 text-[#1A2B39]">Descargas OPUS 22</h2>
              
              <div className="grid md:grid-cols-2 gap-8">
                <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                  <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Módulo 1: Presupuesto Programable</h3>
                  <p className="mb-4 text-gray-600">Descargue la última versión del Módulo 1 de OPUS 22.</p>
                  <div className="flex flex-col gap-3">
                    <Link 
                      to="/soporte/descargas/opus22/m1" 
                      className="inline-flex items-center text-[#0055B8] hover:underline"
                    >
                      <FaDownload className="mr-2" /> Ver detalles y descargar
                    </Link>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                  <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Módulo 2: Planeación y Control Integral</h3>
                  <p className="mb-4 text-gray-600">Descargue la última versión del Módulo 2 de OPUS 22.</p>
                  <div className="flex flex-col gap-3">
                    <Link 
                      to="/soporte/descargas/opus22/m2" 
                      className="inline-flex items-center text-[#0055B8] hover:underline"
                    >
                      <FaDownload className="mr-2" /> Ver detalles y descargar
                    </Link>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                  <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Módulo 3: Administración de Compras</h3>
                  <p className="mb-4 text-gray-600">Descargue la última versión del Módulo 3 de OPUS 22.</p>
                  <div className="flex flex-col gap-3">
                    <Link 
                      to="/soporte/descargas/opus22/m3" 
                      className="inline-flex items-center text-[#0055B8] hover:underline"
                    >
                      <FaDownload className="mr-2" /> Ver detalles y descargar
                    </Link>
                  </div>
                </div>
                
                <div className="bg-white p-6 rounded-lg shadow border border-gray-200">
                  <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Módulo 5: Cuantificación BIM</h3>
                  <p className="mb-4 text-gray-600">Descargue la última versión del Módulo 5 de OPUS 22.</p>
                  <div className="flex flex-col gap-3">
                    <Link 
                      to="/soporte/descargas/opus22/m5" 
                      className="inline-flex items-center text-[#0055B8] hover:underline"
                    >
                      <FaDownload className="mr-2" /> Ver detalles y descargar
                    </Link>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <iframe 
                  src="https://descargas.ecosoft.com.mx/" 
                  title="Portal de Descargas Ecosoft" 
                  className="w-full h-[800px] border rounded shadow-lg"
                  allow="fullscreen"
                />
              </div>
            </div>
          </div>
        );
      case 'adicionales':
        return (
          <div className="py-12 px-6">
            <div className="container mx-auto max-w-6xl">
              <div className="bg-white p-8 rounded-lg shadow-lg border border-gray-200 mb-8">
                <div className="flex items-center mb-6">
                  <div className="bg-[#0055B8]/10 p-3 rounded-full mr-4">
                    <FaInfoCircle className="text-[#0055B8] text-2xl" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-[#1A2B39]">Descargas Adicionales</h2>
                    <p className="text-gray-600">Herramientas y recursos complementarios para OPUS</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Instalador de Matrices Paramétricas</h3>
                    <p className="mb-4 text-gray-600">Compatible con todas las revisiones de OPUS</p>
                    <a 
                      href="/revision/opus15/matrices.zip" 
                      className="inline-flex items-center text-[#0055B8] hover:underline"
                    >
                      <FaDownload className="mr-2" /> Descargar
                    </a>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-xl font-bold mb-4 text-[#0055B8]">SQL Server 2014</h3>
                    <p className="mb-4 text-gray-600">Para utilizar OPUS en modo cliente-servidor</p>
                    <a 
                      href="https://www.microsoft.com/es-mx/download/details.aspx?id=53167" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#0055B8] hover:underline"
                    >
                      <FaDownload className="mr-2" /> Descargar
                    </a>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Administrador de Licencias</h3>
                    <p className="mb-4 text-gray-600">Manual de instalación y configuración</p>
                    <a 
                      href="/descargas/soportec/AdminAW.zip" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#0055B8] hover:underline"
                    >
                      <FaDownload className="mr-2" /> Descargar
                    </a>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
                    <h3 className="text-xl font-bold mb-4 text-[#0055B8]">Guía Llave de Acceso</h3>
                    <p className="mb-4 text-gray-600">Manual de uso y configuración</p>
                    <a 
                      href="/opus21/guia_llave_acceso21.pdf" 
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-[#0055B8] hover:underline"
                    >
                      <FaDownload className="mr-2" /> Descargar
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Descargas OPUS | Ecosoft"
        description="Descargue las últimas versiones de OPUS y sus módulos. Acceda a manuales, guías y recursos adicionales."
        name="Downloads"
        type="website"
      />
      
      {/* Hero Section */}
      <section id="overview" className="relative h-[350px] bg-gradient-to-r from-[#003171] to-[#0066CC]">
        <div className="absolute inset-0 bg-black/30"></div>
        <div className="container mx-auto px-4 h-full flex items-center pt-12 relative z-10">
          <div className="max-w-2xl text-white">
            <h1 className="text-4xl font-bold mb-2">Centro de Descargas</h1>
            <p className="text-lg mb-6">
              Descargue las últimas versiones de OPUS y acceda a todos los recursos necesarios para su instalación y uso.
            </p>
          </div>
        </div>
      </section>
      
      {/* Version Selection Tabs */}
      <div className={`bg-white border-b border-gray-200 ${isSticky ? 'sticky top-16 z-40' : ''}`}>
        <div className="container mx-auto px-4">
          <div className="flex space-x-8 overflow-x-auto">
            <button
              onClick={() => setSelectedVersion('opus25')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                selectedVersion === 'opus25'
                  ? 'border-[#0055B8] text-[#0055B8]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              OPUS 25
            </button>
            <button
              onClick={() => setSelectedVersion('opus24')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                selectedVersion === 'opus24'
                  ? 'border-[#0055B8] text-[#0055B8]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              OPUS 24
            </button>
            <button
              onClick={() => setSelectedVersion('opus22')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                selectedVersion === 'opus22'
                  ? 'border-[#0055B8] text-[#0055B8]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              OPUS 22
            </button>
            <button
              onClick={() => setSelectedVersion('adicionales')}
              className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
                selectedVersion === 'adicionales'
                  ? 'border-[#0055B8] text-[#0055B8]'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Descargas Adicionales
            </button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      {renderVersionContent()}
    </div>
  );
};

export default Downloads; 